<template>
  <div v-if="group">
    <h1>Group</h1>
    <v-btn :to="{ name: 'groups', params: { id: group.typeid } }">
      <v-icon>chevron_left</v-icon>Groups
    </v-btn>
    <v-card>
      <v-card-text>
        <h2>Name</h2>
        <span class="entityName">{{ group.name }}</span>
        <a @click="loadBranches(1)" v-if="edit != 1">change</a>
        <div class="ajaxForm" v-if="edit == 1">
          <h1>Edit name</h1>
          <p>Enter the name in the box below and click Save</p>
          <v-form ref="formGroup" v-model="validGroup" lazy-validation>
            <v-text-field
              label="Name"
              v-model="groupCopy.name"
              :rules="valueRules"
              outline
            />
            <v-btn @click="saveGroup()"> <v-icon>save</v-icon>Save </v-btn>
          </v-form>
          <hr />
          <v-btn @click="edit = 0">Cancel</v-btn>
        </div>
      </v-card-text>
    </v-card>
    <v-card v-if="group.typeid == 8">
      <v-card-text>
        <h2>Panel numbers</h2>
        <v-btn @click="loadBranches(2)" v-if="edit != 2">
          <v-icon>add</v-icon>Panel number
        </v-btn>
        <div class="ajaxForm" v-if="edit == 2">
          <h1>Add panel number</h1>
          <p>
            Select which branches the panel number relates to, enter a panel
            number and click save
          </p>
          <v-form
            ref="formGroupPanel"
            v-model="validGroupPanel"
            lazy-validation
          >
            <v-select
              label="Branches"
              v-model="selectedBranches"
              :items="branches"
              item-value="id"
              item-text="name"
              outline
              multiple
              chips
              :rules="rulesBranches"
            />
            <v-text-field
              label="Panel number"
              v-model="panelNo"
              :rules="valueRules"
              outline
            />
            <v-btn @click="saveGroupPanel()"> <v-icon>save</v-icon>Save </v-btn>
          </v-form>
          <hr />
          <v-btn @click="edit = 0">Cancel</v-btn>
        </div>
        <div v-if="!computedGroupPanels || !computedGroupPanels.length">
          No panel numbers recorded for this group
        </div>
        <div v-if="computedGroupPanels && computedGroupPanels.length">
          <div>{{ `${computedGroupPanels.length} panel numbers` }}</div>
          <v-data-table
            :pagination.sync="paginationGroupPanels"
            :headers="headersGroupPanels"
            :items="computedGroupPanels"
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>
                  <EntityZ
                    :entity="props.item.branch"
                    v-if="props.item.branch"
                    format="system"
                  />
                  <span v-if="!props.item.branch">{{
                    props.item.branchid
                  }}</span>
                </td>
                <td>{{ props.item.panelno }}</td>
                <td>
                  <v-btn @click="archiveGroupPanel(props.item)" small
                    >Archive</v-btn
                  >
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>
    <v-card>
      <v-card-text>
        <h2>Entities in this group</h2>
        <v-data-table
          :pagination.sync="paginationEntities"
          :headers="headersEntities"
          :items="computedEntities"
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr>
              <td>
                <EntityZ :entity="props.item" format="system" />
              </td>
              <td>{{ props.item.address }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <Security :group="group" @triggerUpdate="triggerUpdate" />
  </div>
</template>

<script>
import EntityZ from "@/components/link/EntityZ";
import Security from "@/components/group/Security";
import nbmlaw from "@/api/nbmlaw";
import { convertListOfObjectsToMap } from "@/util/helpers";
export default {
  name: "Group",
  metaInfo() {
    return {
      title: "Group",
      titleTemplate: "%s - Directory - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: {
    EntityZ,
    Security
  },
  computed: {
    computedEntities() {
      if (!this.entities) {
        return undefined;
      }
      return this.entities.map((e) => {
        const address = this.addresses ? this.addresses[e.id] : undefined;
        return { ...e, address };
      });
    },
    computedGroupPanels() {
      if (!this.groupPanels) {
        return undefined;
      }
      const branchesMap = this.branches
        ? convertListOfObjectsToMap(this.branches, "id")
        : undefined;
      return this.groupPanels.map((g) => {
        const branch = branchesMap ? branchesMap[g.branchid] : undefined;
        return { ...g, branch };
      });
    },
    rulesBranches() {
      return [
        this.selectedBranches.length > 0 ||
          "At least one branch should be selected"
      ];
    }
  },
  data() {
    return {
      edit: 0,
      panelNo: null,
      group: null,
      groupCopy: null,
      user: null,
      entities: null,
      addresses: null,
      groupPanels: null,
      validGroup: false,
      validGroupPanel: false,
      valueRules: [(v) => !!v || "Value is required"],
      branches: null,
      selectedBranches: [],
      headersGroupPanels: [
        {
          text: "Branch",
          value: "branch.name"
        },
        {
          text: "Panel number",
          value: "panelNo"
        },
        {
          text: "Archive",
          sortable: false
        }
      ],
      paginationGroupPanels: {
        rowsPerPage: -1,
        sortBy: "address",
        descending: false
      },
      headersEntities: [
        {
          text: "Entity",
          value: "name"
        },
        {
          text: "Address",
          value: "address"
        }
      ],
      paginationEntities: {
        rowsPerPage: -1,
        sortBy: "address",
        descending: false
      }
    };
  },
  methods: {
    async fetchGroup() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}`
      );
      // console.log(response.data);
      this.group = response.data;
    },
    async fetchEntities() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/entities`
      );
      // console.log(response.data);
      this.entities = response.data;
    },
    async fetchAddresses() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/entities/addresses`
      );
      // console.log(response.data);
      this.addresses = response.data;
    },
    async fetchGroupPanels() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/groups/${this.$route.params.id}/groupPanels`
      );
      // console.log(response.data);
      this.groupPanels = response.data;
    },
    async fetchBranches() {
      const response = await nbmlaw.get(`/cors/admin/directory/branches`);
      // console.log(response.data);
      this.branches = response.data;
    },
    loadBranches(edit) {
      this.edit = edit;
      this.panelNo = null;
      this.groupCopy = JSON.parse(JSON.stringify(this.group));
    },
    async archiveGroupPanel(groupPanel) {
      if (confirm("Are you sure?")) {
        await nbmlaw.delete(
          `/cors/admin/directory/groupPanels/${groupPanel.id}`
        );
        // console.log(response.data);
        this.update();
      }
    },
    async saveGroup() {
      if (this.$refs.formGroup.validate()) {
        await nbmlaw.put(
          `/cors/admin/directory/groups/${this.$route.params.id}`,
          this.groupCopy
        );
        // console.log(response.data);
        this.edit = 0;
        this.update();
      }
    },
    async saveGroupPanel() {
      if (this.$refs.formGroupPanel.validate()) {
        await nbmlaw.post(
          `/cors/admin/directory/groups/${this.$route.params.id}/groupPanels`,
          null,
          {
            params: {
              selectedBranches: this.selectedBranches.join("."),
              panelNo: this.panelNo
            }
          }
        );
        // console.log(response.data);
        this.edit = 0;
        this.update();
      }
    },
    async generatePassword() {
      await nbmlaw.put(
        `/cors/admin/directory/groups/${this.$route.params.id}/password`,
        null
      );
      // console.log(response.data);
      this.update();
      this.snackbar = true;
      this.snackbarText = "New password has been generated";
    },
    triggerUpdate() {
      this.update();
    },
    update() {
      this.fetchGroup();
      this.fetchEntities();
      this.fetchAddresses();
      this.fetchGroupPanels();
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.update();
      }
    }
  },
  created() {
    this.fetchBranches();
  }
};
</script>

<style scoped>
.entityName {
  font-size: 2em;
}
</style>
